// Cent-exact money helpers shared by the settlement engine, the cost screens
// and the statement builder. One rounding rule everywhere: an amount is
// rounded half-up on its value in cents (Math.round), never through
// Number.prototype.toFixed, which rounds the binary value and turns
// 252,105 into 252,10 where the engine says 252,11.

const PERCENT = 100;

export const toCents = (amount: number): number => Math.round(amount * 100);

export const fromCents = (cents: number): number => cents / 100;

// Round a euro amount to whole cents.
export const roundCents = (amount: number): number =>
  fromCents(toCents(amount));

// Euro amounts added up in whole cents. A float sum drifts (600 + 634,56 is
// 1234.5599999999999), so a check with a 0,01 tolerance let some one-cent
// differences pass as rounding noise.
export const sumInCents = (amounts: number[]): number =>
  amounts.reduce((sum, amount) => sum + toCents(amount), 0);

// Whether `parts` add up to exactly `total`, to the cent.
export const addsUpTo = (params: { parts: number[]; total: number }): boolean =>
  sumInCents(params.parts) === toCents(params.total);

// Split `amountCents` over `weights` proportionally, in whole cents, such that
// the parts sum to exactly `amountCents`. Zero/negative total weight → all
// zeros (caller decides what to do with the undistributed amount). Leftover
// cents go to the parts with the largest remainders (ties: first wins).
export const splitCents = (
  amountCents: number,
  weights: number[],
): number[] => {
  const totalWeight = weights.reduce((s, w) => s + Math.max(0, w), 0);
  if (totalWeight <= 0) return weights.map(() => 0);
  const raw = weights.map(w => (w > 0 ? (amountCents * w) / totalWeight : 0));
  const floored = raw.map(r => Math.floor(r));
  let leftover = amountCents - floored.reduce((s, f) => s + f, 0);
  const order = raw
    .map((r, i) => ({ i, rem: r - Math.floor(r) }))
    .sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const { i } of order) {
    if (leftover <= 0) break;
    floored[i] += 1;
    leftover -= 1;
  }
  return floored;
};

// The tenant part of a cost the tenant carries `tenantSharePct` % of. One
// cent-exact split of the whole amount (a tie cent goes to the tenant), so the
// owner gets exactly the remainder. Rounding both parts on their own billed or
// lost a cent on every half-cent share.
export const tenantShareCents = (params: {
  totalCents: number;
  tenantSharePct: number;
}): number => {
  const { totalCents, tenantSharePct } = params;
  const [tenantCents] = splitCents(totalCents, [
    tenantSharePct,
    PERCENT - tenantSharePct,
  ]);
  return tenantCents;
};

// VAT on an amount in cents, rounded half-up (half away from zero).
export const vatCents = (params: {
  amountCents: number;
  vatPct: number;
}): number => {
  const { amountCents, vatPct } = params;
  const exact = (Math.abs(amountCents) * vatPct) / PERCENT;
  return Math.sign(amountCents) * Math.round(exact);
};

// VAT on a euro amount, in euros.
export const vatOfAmount = (params: {
  amountExclVat: number;
  vatPct: number;
}): number =>
  fromCents(
    vatCents({
      amountCents: toCents(params.amountExclVat),
      vatPct: params.vatPct,
    }),
  );

// The VAT of one cost spread over the parts it was split into (tenants,
// owners, table rows). The VAT is taken once over the parts together and then
// split in whole cents by their amounts, so the parts' VAT always adds up to
// exactly the VAT of the whole — rounding it per part drifted a cent or two
// away from the supplier's invoice.
//
// Parts of both signs (a departure that settled more than the later
// settlement gives the tenant leaves the owner a credit) are split per sign:
// the negative parts carry the VAT of their own total, the positive parts
// the rest. Weighing every part by its size against the VAT of the net total
// gave the owner VAT with the opposite sign of their credit and billed the
// tenants too little (F02). `totalVatCents` replaces the VAT of the parts'
// total when the caller knows the VAT they must add up to (F34).
export const splitVat = (params: {
  amounts: number[];
  vatPct: number;
  totalVatCents?: number;
}): number[] => {
  const { amounts, vatPct } = params;
  const cents = amounts.map(toCents);
  const totalCents = cents.reduce((s, c) => s + c, 0);
  const totalVatCents =
    params.totalVatCents ?? vatCents({ amountCents: totalCents, vatPct });
  const hasNegative = cents.some(c => c < 0);
  const hasPositive = cents.some(c => c > 0);
  if (!hasNegative || !hasPositive) {
    return splitCents(
      totalVatCents,
      cents.map(c => Math.abs(c)),
    ).map(fromCents);
  }
  const negativeVatCents = vatCents({
    amountCents: cents.reduce((s, c) => s + Math.min(0, c), 0),
    vatPct,
  });
  const negativeVat = splitCents(
    negativeVatCents,
    cents.map(c => Math.max(0, -c)),
  );
  const positiveVat = splitCents(
    totalVatCents - negativeVatCents,
    cents.map(c => Math.max(0, c)),
  );
  // A part of € 0 has no weight in either split, so both give it 0.
  return cents.map((c, i) =>
    fromCents(c < 0 ? negativeVat[i] : positiveVat[i]),
  );
};

// An amount split equally over `parts` in whole cents; the remainder cents
// go to the first parts, so the parts add up to exactly the amount.
export const splitEqually = (params: {
  amount: number;
  parts: number;
}): number[] =>
  splitCents(
    toCents(params.amount),
    Array.from({ length: params.parts }, () => 1),
  ).map(fromCents);
